import { computed, isRef, ref, type Ref } from 'vue';

// eslint-disable-next-line import/prefer-default-export
export const useCurrency = () => {
  type MoneyFieldKey<T> = Extract<keyof T, string>;

  const DEFAULT_MONEY_FIELDS = [ 'amount', 'taxAmount', 'vatRate', 'totalAmount' ] as const;

  const centsToUnits = (cents: number): number => cents / 100;

  const unitsToCents = (units: number): number => Math.round(units * 100);

  const filterValue = (value: number): number | undefined => {
    const str = String(value).replace(',', '.');
    const num = Number.parseFloat(str);

    if (Number.isNaN(num)) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return unitsToCents(num);
  };

  const currencyField = (input: Ref<number> | number): Ref<number> => {
    const baseRef = ref<Ref<number> | number>(input);

    return computed({
      get: () => {
        if (baseRef.value === null || typeof baseRef.value === 'undefined') {
          return baseRef.value;
        }

        return centsToUnits(baseRef.value);
      },
      set: (value: number) => {
        const filteredValue = filterValue(value);

        if (filteredValue) {
          baseRef.value = filteredValue;
        }
      },
    });
  };

  const currencyEntity = <T extends Record<string, unknown>, K extends MoneyFieldKey<T> = MoneyFieldKey<T>>(
    billableRef: Ref<T>,
    moneyFields: K[] = DEFAULT_MONEY_FIELDS as unknown as K[],
  ) => {
    if (!isRef(billableRef)) {
      throw new Error('You must provide a ref() to currencyEntity');
    }

    const fieldsSet = new Set<PropertyKey>(moneyFields);

    return new Proxy({} as Record<string, unknown>, {
      get(_, prop: string) {
        const raw = (billableRef.value as T)[prop as K];

        if (fieldsSet.has(prop) && typeof raw === 'number') {
          return centsToUnits(raw);
        }

        return raw;
      },
      set(_, prop: string, value) {
        if (!fieldsSet.has(prop)) {
          return true;
        }

        const filteredValue = filterValue(value);

        if (typeof filteredValue !== 'undefined') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-param-reassign
          (billableRef.value as any)[prop as K] = filteredValue;
        }

        return true;
      },
      ownKeys() {
        return Reflect.ownKeys(billableRef.value);
      },
      getOwnPropertyDescriptor() {
        return { enumerable: true, configurable: true };
      },
    });
  };

  return {
    currencyField,
    currencyEntity,
    DEFAULT_MONEY_FIELDS,
  };
};
